import { useEffect, useState } from 'react';

import type { AlertReset } from '@/alerts/entities/alert-reset';
import { ListAlertResets } from '@/alerts/view/queries/list-alert-resets';
import { ListAlerts } from '@/alerts/view/queries/list-alerts';
import { appConfig } from '@/config';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useMe } from '@/hooks/use-me';
import { useQueryState } from '@/hooks/useQueryState';
import type { AlertsFilters as Filters } from '@/types/alerts-filters';

export function useAlertsData() {
  const me = useMe();
  const featureFlag = useFeatureFlag();
  const { asset, isMobility, vehicle, assetId } = useCurrentAsset();
  const [isSidebarEmpty, setIsSidebarEmpty] = useState(false);

  const [filters, setFilters] = useQueryState<Filters>(
    'filters',
    Object.assign(
      {},
      appConfig.defaultAlertsFilters,
      me.config.defaultAlertsFilters,
    ),
  );

  const { data: alerts, isLoading } = ListAlerts.useQuery({
    filters: {
      ...filters,
      ...(assetId
        ? { groupIds: !isMobility && asset ? [asset.fullFrontendId] : [] }
        : {}),
    },
    includeTask: featureFlag('taskmanager'),
  });

  const { data: alertResetsData, isLoading: isLoadingAlertResets } =
    ListAlertResets.useQuery({
      filters: { ...filters },
    });

  const alertResets = alertResetsData as AlertReset[];

  useEffect(
    function () {
      if (!isLoading && alerts && alerts.length === 0) {
        setIsSidebarEmpty((prev) => {
          if (!prev) {
            return true;
          }
          return prev;
        });
      }
    },
    [alerts, isLoading],
  );

  const handleSidebarNonEmpty = () => {
    // Reset the empty state when there are alerts
    setIsSidebarEmpty(false);
  };

  const filteredAlerts =
    isMobility && vehicle
      ? alerts?.filter(
          (alert) => alert.systemFrontendId === vehicle.fullFrontendId,
        )
      : alerts;

  return {
    alerts,
    alertResets,
    filteredAlerts,
    filters,
    setFilters,
    isLoading,
    isLoadingAlertResets,
    isSidebarEmpty,
    handleSidebarNonEmpty,
  };
}
